import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';
import { detectStops, totalDistanceKm } from '../utils/locationAnalytics';
import { buildTrailKml, downloadKml } from '../utils/kmlExport';
import { buildVisitReportPdf } from '../utils/visitReportPdf';
import { useCurrency } from '../contexts/CurrencyContext';

/**
 * Live + Trail GPS map for field staff.
 *
 * Two tabs:
 *   • Live   — all users' last known positions on one map, auto-refresh 1 min.
 *   • Trail  — pick a user + date → polyline + start/end markers + stop pins.
 *
 * Free stack: Leaflet + OpenStreetMap tiles (no API key, no rate limits for
 * normal CRM usage). Stops are computed client-side from the existing
 * `/api/locations/history/{user_id}` payload (no extra backend work).
 *
 * Access: gated on `hasPermission(user, 'location_tracking', 'view')` —
 * super-admin / admin short-circuit to true; any other role only sees the
 * page if granted via the user-permissions matrix.
 */

// Default Leaflet marker icons rely on relative image paths that webpack
// doesn't ship — point them at the CDN copies so pins render correctly.
const FixDefaultIcon = () => {
  delete L.Icon.Default.prototype._getIconUrl;
  L.Icon.Default.mergeOptions({
    iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
    iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
    shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  });
  return null;
};

const TRAIL_COLOR = '#2563eb';      // blue-600
const START_COLOR = '#16a34a';      // emerald-600
const END_COLOR = '#dc2626';        // red-600
const STOP_COLOR = '#f59e0b';       // amber-500

// Bahrain default centre (when no data yet)
const FALLBACK_CENTER = [26.2235, 50.5876];

// All three styles served free by CartoDB, English/Latin labels worldwide.
const TILE_STYLES = {
  voyager: {
    label: 'Voyager',
    url: 'https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png',
  },
  light: {
    label: 'Light',
    url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
  },
  dark: {
    label: 'Dark',
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
  },
};
const TILE_STYLE_STORAGE_KEY = 'crm.locationMap.style';

const fmtTime = (ts) => {
  try {
    return new Date(ts).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts || '—';
  }
};

const today = () => new Date().toISOString().slice(0, 10);

// Recenter helper — used after switching user/date so map fits new bounds.
const FitToBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (!bounds || bounds.length === 0) return;
    try {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    } catch {
      /* invalid bounds */
    }
  }, [bounds, map]);
  return null;
};

const LocationMap = () => {
  const { user } = useAuth();
  const { companySettings } = useCurrency();
  const canView = hasPermission(user, 'location_tracking', 'view');
  const [tab, setTab] = useState('live');             // 'live' | 'trail'
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [filterTeam, setFilterTeam] = useState('all');
  const [filterUserId, setFilterUserId] = useState('all');
  const [date, setDate] = useState(today());          // trail tab
  const [endDate, setEndDate] = useState(today());    // trail tab end (range)
  const [livePoints, setLivePoints] = useState([]);   // [{user_id, name, lat, lng, timestamp}]
  const [trailPoints, setTrailPoints] = useState([]); // [{lat, lng, timestamp}]
  const [trailUser, setTrailUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const [tileStyle, setTileStyle] = useState(() => {
    // Hydrate from localStorage so the user's preferred basemap survives
    // navigation / reload.
    try {
      const saved = localStorage.getItem(TILE_STYLE_STORAGE_KEY);
      return saved && TILE_STYLES[saved] ? saved : 'voyager';
    } catch {
      return 'voyager';
    }
  });
  const liveTimerRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem(TILE_STYLE_STORAGE_KEY, tileStyle);
    } catch {
      /* storage unavailable */
    }
  }, [tileStyle]);

  // ── one-time meta loaders ──────────────────────────────────────────
  useEffect(() => {
    if (!canView) return;
    (async () => {
      try {
        const [u, t] = await Promise.all([
          api.get('/users'),
          api.get('/teams').catch(() => ({ data: [] })),
        ]);
        setUsers(u.data || []);
        setTeams(t.data || []);
      } catch {
        /* silent */
      }
    })();
  }, [canView]);

  // ── LIVE tab: load + 60s polling ───────────────────────────────────
  const loadLive = async () => {
    try {
      setLoading(true);
      const res = await api.get('/locations/current');
      const rows = Array.isArray(res.data) ? res.data : [];
      const mapped = rows
        // Existing endpoint shape is `{ user, location }`. Unwrap and skip
        // records with no coords.
        .map((row) => {
          const u = row.user || {};
          const l = row.location || {};
          return {
            user_id: u.id,
            name: u.name || u.email || 'Unknown',
            lat: typeof l.latitude === 'number' ? l.latitude : null,
            lng: typeof l.longitude === 'number' ? l.longitude : null,
            timestamp: l.timestamp,
            address: l.address || '',
          };
        })
        .filter((p) => p.user_id && p.lat != null && p.lng != null);
      setLivePoints(mapped);
      setErr('');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load live positions.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canView) return undefined;
    if (tab !== 'live') return undefined;
    loadLive();
    liveTimerRef.current = setInterval(loadLive, 60_000);
    return () => liveTimerRef.current && clearInterval(liveTimerRef.current);
  }, [tab, canView]);

  // ── TRAIL tab: load on user/date change ────────────────────────────
  const loadTrail = async () => {
    if (filterUserId === 'all') {
      setErr('Please select a user to view their trail.');
      setTrailPoints([]);
      setTrailUser(null);
      return;
    }
    try {
      setLoading(true);
      setErr('');
      const res = await api.get(`/locations/history/${filterUserId}`, {
        params: { start_date: date, end_date: endDate || date },
      });
      const rows = Array.isArray(res.data) ? res.data : [];
      const sorted = rows
        .filter((r) => typeof r.latitude === 'number' && typeof r.longitude === 'number')
        .map((r) => ({ lat: r.latitude, lng: r.longitude, timestamp: r.timestamp }))
        .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      setTrailPoints(sorted);
      setTrailUser(users.find((u) => u.id === filterUserId) || null);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Failed to load trail.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canView) return;
    if (tab !== 'trail') return;
    if (filterUserId === 'all') return;
    loadTrail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, filterUserId, date, endDate, canView]);

  // ── filtering: team scope applies to BOTH tabs ─────────────────────
  const teamMemberIds = useMemo(() => {
    if (filterTeam === 'all') return null;
    return new Set(users.filter((u) => u.team_id === filterTeam).map((u) => u.id));
  }, [users, filterTeam]);

  const userOptions = useMemo(
    () =>
      users
        .filter((u) => !teamMemberIds || teamMemberIds.has(u.id))
        .sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || '')),
    [users, teamMemberIds]
  );

  const visibleLivePoints = useMemo(
    () =>
      livePoints.filter(
        (p) =>
          (filterTeam === 'all' || teamMemberIds?.has(p.user_id)) &&
          (filterUserId === 'all' || p.user_id === filterUserId)
      ),
    [livePoints, teamMemberIds, filterTeam, filterUserId]
  );

  // ── trail-derived analytics ────────────────────────────────────────
  const trailStops = useMemo(
    () => detectStops(trailPoints, { radiusMeters: 60, minDwellMinutes: 5 }),
    [trailPoints]
  );
  const trailKm = useMemo(() => totalDistanceKm(trailPoints), [trailPoints]);

  // ── map bounds ─────────────────────────────────────────────────────
  const bounds = useMemo(() => {
    const all =
      tab === 'live'
        ? visibleLivePoints.map((p) => [p.lat, p.lng])
        : trailPoints.map((p) => [p.lat, p.lng]);
    return all.length ? all : null;
  }, [tab, visibleLivePoints, trailPoints]);

  // ── access gate AFTER hooks so React's hook-order rule is satisfied ──
  if (!canView) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-4" data-testid="locmap-denied">
          🔒 Access denied — your account is not allowed to view location tracking.
        </div>
      </div>
    );
  }

  const initialCenter =
    (bounds && bounds[0]) ||
    (visibleLivePoints[0] && [visibleLivePoints[0].lat, visibleLivePoints[0].lng]) ||
    FALLBACK_CENTER;

  return (
    <div className="p-3 sm:p-4 max-w-7xl mx-auto" data-testid="locmap-page">
      <FixDefaultIcon />

      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">📍 GPS Tracking Map</h1>

        <div className="inline-flex rounded-lg border bg-white overflow-hidden text-sm">
          <button
            type="button"
            onClick={() => setTab('live')}
            className={`px-3 py-1.5 font-medium ${
              tab === 'live' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="locmap-tab-live"
          >
            🟢 Live
          </button>
          <button
            type="button"
            onClick={() => setTab('trail')}
            className={`px-3 py-1.5 font-medium ${
              tab === 'trail' ? 'bg-blue-600 text-white' : 'text-gray-700 hover:bg-gray-50'
            }`}
            data-testid="locmap-tab-trail"
          >
            🧭 Trail
          </button>
        </div>
      </div>

      {/* Filter bar */}
      <div className="bg-white border rounded-lg p-3 mb-3 flex flex-wrap items-end gap-2">
        <div>
          <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">Team</label>
          <select
            value={filterTeam}
            onChange={(e) => {
              setFilterTeam(e.target.value);
              setFilterUserId('all');
            }}
            className="border rounded px-2 py-1 text-sm"
            data-testid="locmap-filter-team"
          >
            <option value="all">All teams</option>
            {teams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">User</label>
          <select
            value={filterUserId}
            onChange={(e) => setFilterUserId(e.target.value)}
            className="border rounded px-2 py-1 text-sm min-w-[160px]"
            data-testid="locmap-filter-user"
          >
            <option value="all">{tab === 'trail' ? 'Pick a user…' : 'All users'}</option>
            {userOptions.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name || u.email}
              </option>
            ))}
          </select>
        </div>

        {tab === 'trail' && (
          <>
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">From</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="border rounded px-2 py-1 text-sm"
                data-testid="locmap-filter-date-from"
              />
            </div>
            <div>
              <label className="block text-[11px] font-semibold text-gray-600 mb-0.5">To</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border rounded px-2 py-1 text-sm"
                data-testid="locmap-filter-date-to"
              />
            </div>
          </>
        )}

        <div className="ml-auto text-xs text-gray-500 self-center" data-testid="locmap-meta">
          {tab === 'live' ? (
            <>
              {visibleLivePoints.length} live · auto-refresh 60 s
            </>
          ) : trailPoints.length ? (
            <>
              {trailPoints.length} pings · {trailKm.toFixed(2)} km · {trailStops.length} stop
              {trailStops.length === 1 ? '' : 's'}
            </>
          ) : (
            'No data'
          )}
        </div>

        {/* Basemap style toggle. Selection persists in localStorage. */}
        <div
          className="inline-flex rounded-md border bg-white overflow-hidden text-[11px] self-center"
          data-testid="locmap-style-toggle"
          role="group"
          aria-label="Map style"
        >
          {Object.entries(TILE_STYLES).map(([key, meta]) => (
            <button
              key={key}
              type="button"
              onClick={() => setTileStyle(key)}
              className={`px-2 py-1 font-medium border-r last:border-r-0 ${
                tileStyle === key
                  ? 'bg-gray-800 text-white'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
              data-testid={`locmap-style-${key}`}
              title={`Switch to ${meta.label} basemap`}
            >
              {meta.label}
            </button>
          ))}
        </div>

        {tab === 'live' && (
          <button
            type="button"
            onClick={loadLive}
            disabled={loading}
            className="px-2.5 py-1 rounded border text-sm hover:bg-gray-50 disabled:opacity-50"
            data-testid="locmap-refresh"
          >
            ⟳
          </button>
        )}
        {tab === 'trail' && (
          <button
            type="button"
            disabled={!trailPoints.length || !trailUser}
            onClick={() => {
              try {
                const kml = buildTrailKml({
                  userName: trailUser?.name || trailUser?.email || 'user',
                  fromDate: date,
                  toDate: endDate || date,
                  points: trailPoints,
                  stops: trailStops,
                  totalKm: trailKm,
                });
                const safeName = (trailUser?.name || 'trail').replace(/[^\w-]+/g, '_');
                const safeDates = endDate && endDate !== date ? `${date}_to_${endDate}` : date;
                downloadKml(kml, `${safeName}_${safeDates}.kml`);
              } catch (e) {
                setErr(e?.message || 'Failed to build KML.');
              }
            }}
            className="px-2.5 py-1 rounded border border-emerald-300 text-emerald-700 text-sm font-medium hover:bg-emerald-50 disabled:opacity-50"
            title={
              !trailPoints.length
                ? 'Load a trail with at least one ping to export'
                : 'Download trail as KML (opens in Google Earth / Maps)'
            }
            data-testid="locmap-export-kml"
          >
            ⬇ KML
          </button>
        )}
        {tab === 'trail' && (
          <button
            type="button"
            disabled={!trailPoints.length || !trailUser || pdfBusy}
            onClick={async () => {
              try {
                setPdfBusy(true);
                setErr('');
                // html2canvas works against the live Leaflet div on screen.
                // We look it up by class so we don't need a separate ref.
                const mapEl = document.querySelector('.leaflet-container');
                await buildVisitReportPdf({
                  userName: trailUser?.name || trailUser?.email || 'user',
                  fromDate: date,
                  toDate: endDate || date,
                  points: trailPoints,
                  stops: trailStops,
                  totalKm: trailKm,
                  mapEl,
                  companyName: companySettings?.company_name || '',
                });
              } catch (e) {
                setErr(e?.message || 'Failed to build PDF.');
              } finally {
                setPdfBusy(false);
              }
            }}
            className="px-2.5 py-1 rounded border border-rose-300 text-rose-700 text-sm font-medium hover:bg-rose-50 disabled:opacity-50"
            title={
              !trailPoints.length
                ? 'Load a trail with at least one ping to export'
                : 'Generate a customer-ready PDF with map + stop table'
            }
            data-testid="locmap-export-pdf"
          >
            {pdfBusy ? '… PDF' : '⬇ PDF'}
          </button>
        )}
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-2 mb-2" data-testid="locmap-error">
          {err}
        </div>
      )}

      <div className="rounded-lg border overflow-hidden bg-white" style={{ height: '70vh' }}>
        <MapContainer
          center={initialCenter}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
          scrollWheelZoom
          data-testid="locmap-canvas"
        >
          <TileLayer
            key={tileStyle}
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url={TILE_STYLES[tileStyle].url}
            subdomains={['a', 'b', 'c', 'd']}
          />

          {/* LIVE tab — one pin per user */}
          {tab === 'live' &&
            visibleLivePoints.map((p) => (
              <Marker key={p.user_id} position={[p.lat, p.lng]}>
                <Popup>
                  <div className="text-xs">
                    <div className="font-semibold text-gray-900">{p.name}</div>
                    {p.address && <div className="text-gray-600 mt-1">{p.address}</div>}
                    <div className="text-gray-500 mt-1">{fmtTime(p.timestamp)}</div>
                  </div>
                </Popup>
              </Marker>
            ))}

          {/* TRAIL tab — polyline + start/end + stops */}
          {tab === 'trail' && trailPoints.length > 1 && (
            <Polyline
              positions={trailPoints.map((p) => [p.lat, p.lng])}
              pathOptions={{ color: TRAIL_COLOR, weight: 4, opacity: 0.85 }}
            />
          )}
          {tab === 'trail' && trailPoints.length >= 1 && (
            <CircleMarker
              center={[trailPoints[0].lat, trailPoints[0].lng]}
              radius={9}
              pathOptions={{ color: START_COLOR, fillColor: START_COLOR, fillOpacity: 1 }}
            >
              <Popup>
                <div className="text-xs">
                  <div className="font-semibold text-emerald-700">Start</div>
                  <div>{fmtTime(trailPoints[0].timestamp)}</div>
                </div>
              </Popup>
            </CircleMarker>
          )}
          {tab === 'trail' && trailPoints.length >= 2 && (
            <CircleMarker
              center={[
                trailPoints[trailPoints.length - 1].lat,
                trailPoints[trailPoints.length - 1].lng,
              ]}
              radius={9}
              pathOptions={{ color: END_COLOR, fillColor: END_COLOR, fillOpacity: 1 }}
            >
              <Popup>
                <div className="text-xs">
                  <div className="font-semibold text-red-700">End</div>
                  <div>{fmtTime(trailPoints[trailPoints.length - 1].timestamp)}</div>
                </div>
              </Popup>
            </CircleMarker>
          )}
          {tab === 'trail' &&
            trailStops.map((s, i) => (
              <CircleMarker
                // eslint-disable-next-line react/no-array-index-key
                key={`stop-${i}`}
                center={[s.lat, s.lng]}
                radius={11}
                pathOptions={{ color: STOP_COLOR, fillColor: STOP_COLOR, fillOpacity: 0.9 }}
              >
                <Popup>
                  <div className="text-xs">
                    <div className="font-semibold text-amber-700">
                      Stop · {s.durationMinutes} min
                    </div>
                    <div>From {fmtTime(s.startedAt)}</div>
                    <div>To {fmtTime(s.endedAt)}</div>
                  </div>
                </Popup>
              </CircleMarker>
            ))}

          <FitToBounds bounds={bounds} />
        </MapContainer>
      </div>

      {tab === 'trail' && trailUser && (
        <div className="text-xs text-gray-600 mt-2" data-testid="locmap-trail-meta">
          Showing trail for <b>{trailUser.name || trailUser.email}</b> · {date}
          {endDate && endDate !== date ? ` → ${endDate}` : ''} · {trailKm.toFixed(2)} km · {trailStops.length} stops
        </div>
      )}
    </div>
  );
};

export default LocationMap;
